import { Observable } from 'rxjs';
import * as Sentry from '@sentry/react';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'refresh_token';

export class HttpError extends Error {
  constructor(public status: number, public data: unknown) {
    super(`HTTP ${status}`);
  }
}

export function formatDrfError(error: unknown, fallback: string): string {
  if (!(error instanceof HttpError) || !error.data || typeof error.data !== 'object') return fallback;
  const data = error.data as Record<string, unknown>;
  const messages: string[] = [];
  for (const [field, value] of Object.entries(data)) {
    const errors = Array.isArray(value) ? value : [value];
    const prefix = field === 'non_field_errors' || field === 'detail' ? '' : `${field}: `;

    messages.push(...errors.map(e => `${prefix} ${e}`));
  }
  return messages.length > 0 ? messages.join('\n') : fallback;
}

function buildHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function tryRefreshToken(): Promise<string | null> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;
  try {
    const res = await fetch(`${BASE_URL}/api/v1/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return null;
    const data = await res.json() as { access: string };
    localStorage.setItem(TOKEN_KEY, data.access);
    return data.access;
  } catch {
    return null;
  }
}

async function fetchJson<T>(url: string, init: RequestInit, retry = true): Promise<T> {
  const res = await fetch(url, init);

  if (res.status === 401 && retry) {
    const newToken = await tryRefreshToken();
    if (newToken) {
      const retryHeaders = { ...(init.headers as Record<string, string>), Authorization: `Bearer ${newToken}` };
      return fetchJson<T>(url, { ...init, headers: retryHeaders }, false);
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    window.location.href = '/login';
    throw new HttpError(401, null);
  }

  if (!res.ok) {
    let data: unknown;
    try { data = await res.json(); } catch { data = null; }
    throw new HttpError(res.status, data);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export function get<T>(path: string): Observable<T> {
  return new Observable<T>(subscriber => {
    fetchJson<T>(`${BASE_URL}${path}`, { headers: buildHeaders() })
      .then(data => { subscriber.next(data); subscriber.complete(); })
      .catch(err => { if (!(err instanceof HttpError && err.status === 401)) Sentry.captureException(err); subscriber.error(err); });
  });
}

export function post<T>(path: string, body: unknown): Observable<T> {
  return new Observable<T>(subscriber => {
    fetchJson<T>(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(body),
    })
      .then(data => { subscriber.next(data); subscriber.complete(); })
      .catch(err => { if (!(err instanceof HttpError && err.status === 401)) Sentry.captureException(err); subscriber.error(err); });
  });
}
